import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Department } from '@/pages/Departments';

interface DepartmentComboboxProps {
    selectedId?: string | null;
    onChange: (department: Department | null) => void;
    disabled?: boolean;
}

const DepartmentCombobox: React.FC<DepartmentComboboxProps> = ({ selectedId, onChange, disabled }) => {
    const [query, setQuery] = useState('');
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'departments'));
                const departmentsData = querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as Department))
                    .sort((a, b) => a.completeDescription.localeCompare(b.completeDescription));
                setDepartments(departmentsData);
            } catch (error) {
                console.error("Error fetching departments: ", error);
            }
        };

        fetchDepartments();
    }, []);

    useEffect(() => {
        if (departments.length > 0) {
            const found = departments.find(d => d.id === selectedId);
            setSelectedDepartment(found || null);
        }
    }, [selectedId, departments]);

    const filteredDepartments =
        query === ''
            ? departments
            : departments.filter(department =>
                department.completeDescription.toLowerCase().includes(query.toLowerCase())
            );

    const handleSelectionChange = (department: Department | null) => {
        setSelectedDepartment(department);
        onChange(department);
    };

    return (
        <Combobox as="div" value={selectedDepartment} onChange={handleSelectionChange} disabled={disabled} className="mt-1">
            <div className="relative">
                <Combobox.Input
                    className="input-primary w-full"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(department: Department | null) => department?.completeDescription || ''}
                    placeholder="Selecione um departamento"
                    autoComplete="off"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>

                {filteredDepartments.length > 0 && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredDepartments.map((department) => (
                            <Combobox.Option
                                key={department.id}
                                value={department}
                                className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                            >
                                {({ active, selected }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                                            {department.completeDescription}
                                        </span>
                                        {selected && (
                                            <span
                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
        </Combobox>
    );
};

export default DepartmentCombobox; 